import React, { useState } from 'react';
import { Button, Alert } from 'antd';
import { LockOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { useMsal } from '@azure/msal-react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../stores/auth-store';
import AccessGateLocal from './AccessGateLocal';
import './AccessGate.css';

const LOCAL_GATE_HOSTS = new Set([
  'localhost',
  '127.0.0.1',
  'ai-ops-nonprod.coxautoinc.com',
  'ai-ops-st.coxautoinc.com',
]);

export function shouldRenderLocalGate({ hostname, search }) {
  if (!LOCAL_GATE_HOSTS.has(hostname)) return false;
  const params = new URLSearchParams(search);
  return params.get('mode') === 'user';
}

function AccessGate() {
  const location = useLocation();

  if (shouldRenderLocalGate({ hostname: window.location.hostname, search: location.search })) {
    return <AccessGateLocal />;
  }

  return <AccessGateSSO />;
}

function AccessGateSSO() {
  const { loginWithMicrosoft, authError } = useAuth();
  const { instance: msalInstance } = useMsal();
  const [ssoLoading, setSsoLoading] = useState(false);
  const [localError, setLocalError] = useState('');

  const handleMicrosoftLogin = async () => {
    if (ssoLoading) return;
    setLocalError('');
    setSsoLoading(true);
    try {
      await loginWithMicrosoft(msalInstance);
    } catch (err) {
      setLocalError(err?.message || 'Microsoft login failed');
      setSsoLoading(false);
    }
  };

  const displayError = localError || authError || '';

  return (
    <div className="access-gate-overlay">
      <div className="login-card">
        {/* Lock Icon */}
        <div className="login-lock-icon">
          <LockOutlined />
        </div>

        {/* Title */}
        <h1 className="login-title">Sign in to Next-Gen Platform Hub</h1>

        {/* Subtitle */}
        <p className="login-subtitle">
          Use your corporate account to access Next-Gen Platform Hub.
        </p>

        {/* Error */}
        {displayError && (
          <Alert type="error" showIcon message={displayError}
            style={{ marginBottom: 16, borderRadius: 8, textAlign: 'left' }}
            closable onClose={() => setLocalError('')} />
        )}

        {/* SSO Button */}
        <Button
          size="large"
          block
          onClick={handleMicrosoftLogin}
          loading={ssoLoading}
          className="sso-btn"
        >
          <svg className="microsoft-logo" viewBox="0 0 21 21" xmlns="http://www.w3.org/2000/svg">
            <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
            <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
            <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
            <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
          </svg>
          {ssoLoading ? 'Connecting...' : 'Sign in with Microsoft Entra'}
        </Button>

        {/* Info Divider */}
        <div className="info-divider">
          <span className="info-divider-text">INFO</span>
        </div>

        {/* Info Box */}
        <div className="info-box">
          <InfoCircleOutlined className="info-box-icon" />
          <p className="info-box-text">
            Sign in requires your Cox Automotive corporate credentials via Microsoft Entra ID.
            Contact IT Support if you're unable to access your account.
          </p>
        </div>
      </div>
    </div>
  );
}

export default AccessGate;
